// Agent Task Types
export type {
  AgentTaskStatus,
  AgentTask,
  AgentTaskEventData,
} from './agent-tasks';

// Knowledge Base Types
export type {
  KbScope,
  IndexStatus,
  KnowledgeBase,
  CreateKnowledgeBaseInput,
  VfsNode,
  VfsDerivative,
  KbSearchResult,
  KbSearchResponse,
  KnowledgeBaseGrant,
  KbPaginated,
} from './kb';
import type { KnowledgeBaseGrant } from './kb';
import type { AskUserAnswer, AskUserRef } from './inbox';

// Plugin Types
export type {
  Plugin,
  PluginStatus,
  PluginRefType,
  PluginMode,
  PluginHost,
  PluginVisibility,
  PluginAuthor,
  PluginSource,
  PluginUpstream,
  PluginMcpServerReport,
  PluginSkippedItem,
  PluginResources,
  PluginResourcesExpanded,
  InstallPluginInput,
  InstallPluginMetadata,
  PluginInstallAck,
  PluginInstallReport,
  PluginInstallResourceOutcome,
  PluginInstallResult,
  SyncAckResponse,
  PluginProvenance,
  PluginErrorCode,
  PluginErrorBody,
  ExploreRequest,
  ExploreResponse,
  ExploreCandidate,
  ExploreCandidateSource,
  ExploreMarketplaceInfo,
} from './plugins';

import type { PluginProvenance } from './plugins';

// Gateway Types
export type {
  Gateway,
  GatewayStatus,
  CreateGatewayInput,
  UpdateGatewayInput,
  GatewayTestResult,
  GatewayTool,
  GatewaySyncResult,
} from './gateways';

// MCP Server Types (per-space external MCP servers with per-user OAuth)
export type {
  McpServer,
  McpServerStatus,
  McpCreateErrorCode,
  ManualClient,
  CreateMcpServerInput,
  UpdateMcpServerInput,
  McpSyncResult,
  McpAuthStatus,
  McpAuthRequiredResult,
  McpAuthMethod,
  McpApiKeyHeaderSpec,
  McpAuthMode,
  McpCredentialLevel,
} from './mcp';

// Subscription Types
export type {
  SubscriptionStatus,
  SubscriptionScope,
  SubscriptionDeliveryMode,
  SubscriptionPriority,
  EventSubscriptionFilter,
  EventSubscriptionDelivery,
  EventSubscription,
  ListSubscriptionsParams,
} from './subscriptions';

// Model Registry Types
export type ModelStatus = 'active' | 'deprecated' | 'preview';
export type ModelCapability = 'text' | 'vision' | 'reasoning' | 'code';

export interface ParameterSupport {
  temperature?: { supported: boolean; min?: number; max?: number; default?: number };
  topP?: { supported: boolean; min?: number; max?: number };
  reasoning?: {
    supported: boolean;
    minBudgetTokens?: number;
    maxBudgetTokens?: number;
    defaultBudgetTokens?: number;
    forbidsTemperature?: boolean;
  };
  maxTokens?: { min?: number; max?: number; default?: number };
}

export type ModelProvider = 'bedrock' | 'openai' | 'xai';

export interface PlatformModel {
  modelId: string;
  providerModelId: string;
  provider: ModelProvider;
  displayName: string;
  description?: string;
  contextLimit: number;
  parameterSupport: ParameterSupport;
  capabilities: ModelCapability[];
  status: ModelStatus;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ListModelsResponse {
  data: PlatformModel[];
  pagination: { count: number; nextCursor: string | null; hasMore: boolean };
}

// Agent Types
export type AgentStatus = 'active' | 'inactive' | 'archived';

/** Backend execution runtime. Absent ⇒ 'agentcore'. */
export type AgentRuntime = 'agentcore' | 'claude';

export interface ToolReference {
  toolId: string;
  name: string;
  group?: string;
}

export interface StaticEventSubscription {
  eventType?: string;
  source?: string;
  subjectType?: string;
  subjectId?: string;
  delivery?: { mode: 'in_session' | 'trigger' };
}

/** Per-agent realtime voice configuration. Only meaningful when
 *  `enableVoice` is on. */
export interface AgentVoiceConfig {
  provider: 'grok';
  /** Provider voice, e.g. 'ara'. Omit for the provider default. */
  voiceId?: string;
  /** BCP-47 language hint, e.g. 'en-US'. */
  language?: string;
  /** Session cap in minutes (backend default 30). */
  maxSessionMinutes?: number;
}

export interface Agent {
  agentId: string;
  orgId: string;
  userId: string;
  name: string;
  description?: string;
  modelProvider: ModelProvider;
  modelId: string;
  systemPrompt: string;
  /** Backend execution runtime. Absent ⇒ 'agentcore'. */
  runtime?: AgentRuntime;
  maxTokens?: number;
  maxIterations?: number;
  temperature?: number;
  status: AgentStatus;
  skillIds?: string[];
  toolIds?: string[];
  subAgentIds?: string[];
  knowledgeBases?: KnowledgeBaseGrant[];
  tools?: ToolReference[];
  accessScope?: 'global';
  platformCortexVersion?: number;
  eventSubscriptions?: StaticEventSubscription[];
  enableCodeInterpreter?: boolean;
  enableCaching?: boolean;
  enableAui?: boolean;
  /** Allow the ask_user / human-in-the-loop tool. Defaults on; only `false` removes it. */
  enableAskUser?: boolean;
  /** Realtime voice sessions (needs an xAI key connected — org, space, or
   *  personal tier; sessions fail at start with `VOICE_CREDENTIAL_MISSING`
   *  without one). */
  enableVoice?: boolean;
  voice?: AgentVoiceConfig;
  createdAt: string;
  updatedAt: string;
  latestVersion?: number;
  defaultAlias?: string;
  /** Owning space — returned on cross-space list endpoints (e.g. /me/agents). */
  spaceId?: string;
  /** Owning space display name — returned alongside spaceId on /me/agents. */
  spaceName?: string;
  /** Set when the entity was installed by a plugin. */
  provenance?: PluginProvenance;
}

export interface CreateAgentInput {
  name: string;
  description?: string;
  modelProvider: ModelProvider;
  modelId: string;
  systemPrompt: string;
  runtime?: AgentRuntime;
  maxTokens?: number;
  maxIterations?: number;
  temperature?: number;
  skillIds?: string[];
  toolIds?: string[];
  subAgentIds?: string[];
  knowledgeBases?: KnowledgeBaseGrant[];
  enableCodeInterpreter?: boolean;
  enableCaching?: boolean;
  enableAui?: boolean;
  enableAskUser?: boolean;
  enableVoice?: boolean;
  voice?: AgentVoiceConfig;
}

export interface UpdateAgentInput {
  name?: string;
  description?: string;
  modelProvider?: ModelProvider;
  modelId?: string;
  systemPrompt?: string;
  runtime?: AgentRuntime;
  maxTokens?: number;
  maxIterations?: number;
  temperature?: number;
  status?: AgentStatus;
  skillIds?: string[];
  toolIds?: string[];
  subAgentIds?: string[];
  knowledgeBases?: KnowledgeBaseGrant[];
  platformCortexVersion?: number;
  enableCodeInterpreter?: boolean;
  enableCaching?: boolean;
  enableAui?: boolean;
  enableAskUser?: boolean;
  enableVoice?: boolean;
  voice?: AgentVoiceConfig;
}

export interface AgentVersion {
  agentId: string;
  version: number;
  snapshot: Agent;
  description?: string;
  publishedBy: string;
  publishedAt: string;
}

export interface AgentAlias {
  agentId: string;
  aliasName: string;
  targetVersion: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// Skill Types
export type SkillStatus = 'active' | 'inactive' | 'archived';

export interface Skill {
  skillId: string;
  orgId: string;
  userId: string;
  name: string;
  description: string;
  instructions: string;
  status: SkillStatus;
  createdAt: string;
  updatedAt: string;
  /** Set when the skill was installed by a plugin. */
  provenance?: PluginProvenance;
}

export interface CreateSkillInput {
  name: string;
  description: string;
  instructions: string;
}

export interface UpdateSkillInput {
  name?: string;
  description?: string;
  instructions?: string;
  status?: SkillStatus;
}

// Tool Types
export type ToolStatus = 'active' | 'inactive' | 'archived';
export type ToolType = 'platform' | 'mcp';
export type McpTransport = 'sse' | 'websocket';

export interface ToolInputProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  enum?: string[];
  items?: ToolInputProperty;
  default?: unknown;
}

export interface ToolInputSchema {
  type: 'object';
  properties: Record<string, ToolInputProperty>;
  required?: string[];
}

export interface BaseTool {
  toolId: string;
  orgId: string;
  userId: string;
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
  type: ToolType;
  status: ToolStatus;
  accessScope?: 'global';
  source?: 'manual' | 'gateway';
  gatewayId?: string;
  gatewayName?: string;
  group?: string;
  /** Brand/product slug for the group, resolves to a `/logos/<slug>-icon.svg`
   *  via ProviderLogo's cascade (e.g. `optr-activity` → `optr`). */
  groupSlug?: string;
  displayOrder?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformTool extends BaseTool {
  type: 'platform';
  handlerId: string;
  handlerConfig?: Record<string, unknown>;
}

export interface McpTool extends BaseTool {
  type: 'mcp';
  serverUrl: string;
  transport: McpTransport;
  remoteToolName: string;
  credentialId?: string;
  timeoutMs?: number;
}

export type Tool = PlatformTool | McpTool;

export interface ToolGroup {
  group: string;
  /** Brand/product slug shared by the group's tools (e.g. `optr-activity`). */
  groupSlug?: string;
  tools: Tool[];
}

export interface GroupedToolsResponse {
  groups: ToolGroup[];
}

export interface CreatePlatformToolInput {
  type: 'platform';
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
  handlerId: string;
  handlerConfig?: Record<string, unknown>;
  group?: string;
  displayOrder?: number;
}

export interface CreateMcpToolInput {
  type: 'mcp';
  name: string;
  description: string;
  inputSchema: ToolInputSchema;
  serverUrl: string;
  transport: McpTransport;
  remoteToolName: string;
  credentialId?: string;
  timeoutMs?: number;
  group?: string;
  displayOrder?: number;
}

export type CreateToolInput = CreatePlatformToolInput | CreateMcpToolInput;

export interface UpdateToolInput {
  name?: string;
  description?: string;
  inputSchema?: ToolInputSchema;
  status?: ToolStatus;
  handlerId?: string;
  handlerConfig?: Record<string, unknown>;
  serverUrl?: string;
  transport?: McpTransport;
  remoteToolName?: string;
  credentialId?: string;
  timeoutMs?: number;
  group?: string;
  displayOrder?: number;
}

// User/Auth Types
export interface UserPreferences {
  builderConversationId?: string;
  sandboxConversationId?: string;
  sandboxAgentId?: string;
  theme?: 'light' | 'dark' | 'system';
  lastTopicConversationId?: string;
  /** The space the user was last viewing in the console. Used as the default
   *  target when re-entering /console with no `:spaceId` in the URL. */
  lastSpaceId?: string;
  operatorChatWidth?: number;
  builderChatWidth?: number;
  /** Inbox label rail: how the label list is ordered. Defaults to 'color'. */
  inboxLabelSort?: 'az' | 'za' | 'color';
  /** Inbox label rail: space ids whose labels are hidden from the rail. */
  inboxHiddenSpaceIds?: string[];
}

export interface ExtendedProfile {
  title?: string;
  department?: string;
  manager?: string;
}

export interface User {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  timezone?: string;
  role: 'admin' | 'user' | 'viewer';
  orgId: string;
  permissions: string[];
  preferences?: UserPreferences;
  extendedProfile?: ExtendedProfile;
  /** Set by backend at provisioning time — the user's Personal Space. Used by
   * the console to resolve the default landing space. */
  defaultSpaceId?: string;
  /** Active picture URL — render the avatar from this; null/absent → initials. */
  avatarUrl?: string;
  /** Provider slug of the currently active picture (e.g. "google", "slack"). */
  activeProfilePictureSource?: string;
  /** All available pictures, one per connected provider. */
  profilePictures?: ProfilePicture[];
}

/** A provider-supplied avatar. Picture management is provider-driven — there is
 * no custom upload; the user activates one of their connected providers' images. */
export interface ProfilePicture {
  /** Provider slug: "google" | "slack" | "x" | "gitlab" | ... */
  source: string;
  /** Directly-embeddable image URL. */
  url: string;
  /** ISO-8601 timestamp of the last push from the provider. */
  updatedAt: string;
}

export interface SignInInput {
  email: string;
  password: string;
}

export interface SignUpInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  expiresIn?: number;
  error?: string;
  code?: string;
}

// Invoke/Test Types
export interface InvokeInput {
  message: string;
  version?: number;
  alias?: string;
  conversationId?: string;
  context?: {
    timezone?: string;
    [key: string]: unknown;
  };
  /**
   * Surfaces the user #-mentioned in the composer. Forwarded by optr-api to
   * agent-core as `context.userSurfaceRefs` so the operator agent can decide
   * to delegate in-place updates by passing them as `surfaceRefs` to the
   * delegate platform tool.
   */
  surfaceRefs?: SurfaceRef[];
  /**
   * Agents the user @-mentioned in the composer. Forwarded as a routing hint
   * so a coordinator can prefer the named agent when delegating.
   */
  agentRefs?: AgentRef[];
  /**
   * Files the user attached to this message via the composer (drag-drop or
   * the `+` action). Frontend uploads them to the conversation VFS first
   * (`uploads/<name>`) and the agent receives the resolved metadata here,
   * so it can `read_file(path)` with its existing VFS tool.
   */
  fileRefs?: FileRef[];
  /**
   * ask_user answer riding on an invoke (mirrors the direct-WS prompt
   * frame): when the inline card answers over a REST fallback, these make
   * invoke-async run the ask_user closeout (resolve the request + inbox
   * item, persist the answer `askUserRef`) instead of degrading the answer
   * to a plain message.
   */
  askUserRequestId?: string;
  answers?: AskUserAnswer[];
}

/** One file attached to a composer message. By default the file body lives
 *  in the conversation's VFS at `path` by the time invokeAsync is called;
 *  when `kbId` is set the file instead lives in that knowledge base. */
export interface FileRef {
  path: string;
  name: string;
  /** Knowledge-base owner (`kbs-…`) when the ref came from a `!`-mention of
   *  a KB file. Undefined → the file lives in the conversation VFS and is
   *  resolved by `path` (as today). Present → the backend resolves `path`
   *  against this KB instead. See `context/kb-file-mention-contract.md`. */
  kbId?: string;
  /** Set by upload attachments; omitted on `!`-mention file refs (the
   *  agent resolves the file by `path`, so descriptive metadata is not
   *  load-bearing for those). */
  size?: number;
  contentType?: string;
  fileId?: string;
}

/** Two-id pair the frontend sends to identify a surface to update in place. */
export interface SurfaceRef {
  canvasId: string;
  surfaceId: string;
}

/** Agent the user picked from the @-mention popover. */
export interface AgentRef {
  agentId: string;
  agentName: string;
}

/**
 * Surface reference resolved by the backend (from a SurfaceRef) into
 * display-ready fields for the frontend to render back-links on completion.
 */
export interface ResolvedSurfaceRef {
  canvasId: string;
  surfaceId: string;
  surfaceName: string;
  title?: string;
  version?: number;
}

export interface InvokeResponse {
  response: string;
  conversationId: string;
  toolCalls?: ToolCall[];
}

export interface ToolCall {
  toolName: string;
  toolDisplayName?: string;
  toolCallId: string;
  input: Record<string, unknown>;
  output?: unknown;
  error?: string;
  durationMs?: number;
  /** Backend-authored present-continuous CoT label (e.g. "Reading README.md"). */
  label?: string;
}

// Run Step Types (ordered execution trace)
export interface RunStepResponse {
  type: 'narration' | 'tool_call' | 'reasoning';
  iteration: number;
  timestamp: string;
  usage?: { inputTokens: number; outputTokens: number; totalTokens: number };
  text?: string;
  textUrl?: string;
  toolName?: string;
  toolDisplayName?: string;
  toolCallId?: string;
  input?: Record<string, unknown>;
  output?: string;
  outputUrl?: string;
  error?: string;
  artifacts?: ArtifactReference[];
  durationMs?: number;
  /** Backend-authored present-continuous CoT label (persisted on tool_call steps). */
  label?: string;
}

// Async Invoke Types
export interface InvokeAsyncResponse {
  runId: string;
  conversationId: string;
  status: 'pending' | 'running' | 'completed' | 'error';
}

/**
 * Ticket for a direct, per-run WebSocket to the conversation's warm micro-VM.
 * Returned by `POST /conversations/{conversationId}/ws-presign`. Presign only
 * opens the connection — no run is created and no runId is issued here (the
 * runId arrives later on the `accepted` frame). The ticket gates the handshake
 * only (valid until `expiresAt`, ~5 min); once connected the socket stays open
 * across turns. See `src/utils/direct-run-connection.ts`.
 */
export interface WsPresignTicket {
  /** Host-less, SigV4-signed WebSocket path (e.g. `/runtimes/<arn>/ws?…`).
   *  Connect with `new WebSocket(wsPath)` — it resolves same-origin and
   *  upgrades https→wss; CloudFront proxies `/runtimes/*` to AgentCore.
   *  Treat as a ~5-min handshake credential: HTTPS only, never log. */
  wsPath: string;
  conversationId: string;
  /** AgentCore micro-VM session id this socket lands on. */
  sessionId: string;
  correlationId: string;
  /** Epoch milliseconds after which the ticket can no longer open a connection
   *  (`new Date(expiresAt)`). Tolerates an ISO string defensively. */
  expiresAt: number | string;
}

// Dispatch Action Types — used by AUI surface action buttons. The frontend
// sends raw action data to a dedicated `/dispatch-action` endpoint; optr-api
// constructs the synthetic prompt and tags the persisted user message with
// `source: 'aui_action'` so it can be filtered out of the conversation thread.
export interface DispatchActionInput {
  conversationId?: string;
  /**
   * Agent-provided surface name (NOT the `srf-…` surfaceId). The backend's
   * `/dispatch-action` handler requires this — it only uses surfaceName +
   * canvasId to build the synthetic prompt and rejects the request with
   * "surfaceName is required" when it's missing.
   */
  surfaceName: string;
  canvasId: string;
  action: {
    name: string;
    label: string;
    payload?: Record<string, unknown>;
  };
}

export type DispatchActionResponse = InvokeAsyncResponse;

export interface RetryRunResponse {
  runId: string;
  conversationId: string;
  status: string;
  correlationId: string;
  retriedFromRunId: string;
}

/**
 * `incomplete` is a non-error terminal: the agent ran out of agentic-loop
 * iterations and never produced a final answer, so `response` holds a progress
 * summary and (when `resumable`) the next run in the conversation continues
 * from a checkpoint. Render it as a pause, never as a failure.
 */
export type RunStatus =
  | 'pending'
  | 'running'
  | 'awaiting_input'
  | 'completed'
  | 'failed'
  | 'error'
  | 'cancelled'
  | 'incomplete';

/**
 * Identifies the agent-core runtime container that served a run. Stamped in
 * two phases (build identity at startRun, sessionId after AgentCore returns);
 * absent on rows created before the backend started emitting it.
 * See `parseBuildTime` in `src/lib/build-info.ts`.
 */
export interface RunRuntime {
  /** AgentCore micro-VM session ID. Internal — not surfaced in UI today. */
  sessionId?: string;
  /** Semver from the agent-core service's package.json — bumped per release. */
  version?: string;
  /** "<short-git-sha>-<base36 unix-seconds>", e.g. "000403a-tf7yug". */
  build?: string;
}

export interface RunStatusResponse {
  runId: string;
  conversationId: string;
  agentId: string;
  /** Resolved agent display name. Returned on list endpoints; absent on legacy single-run reads. */
  agentName?: string;
  status: RunStatus;
  response?: string;
  /** Truncated response (~200 chars) returned by list endpoints so the UI can render a preview without the full payload. */
  responsePreview?: string;
  steps?: RunStepResponse[];
  currentIteration?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  durationMs?: number;
  usage?: TokenUsage;
  error?: string;
  errorCode?: string;
  /** Why the run stopped, when status is 'incomplete' */
  stopReason?: string;
  /** True when a follow-up run continues from this run's checkpoint */
  resumable?: boolean;
  parentRunId?: string;
  childRunIds?: string[];
  executionMode?: 'coordinator' | 'subagent' | 'delegated' | 'standalone' | 'direct';
  /** Who/what initiated this run. */
  triggerSource?: 'user' | 'agent' | 'event' | 'scheduler';
  /** Owning space. Returned on cross-space list endpoints (e.g. /me/runs). */
  spaceId?: string;
  /** Agent-core runtime container that served this run. Absent on legacy rows. */
  runtime?: RunRuntime;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  nextToken?: string;
  totalCount?: number;
}

// Connection Types — Email
export interface Email {
  emailId: string;
  orgId: string;
  userId: string;
  emailAddress: string;
  allowedSenders: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailInput {
  localPart?: string;
  allowedSenders?: string[];
}

export interface UpdateEmailInput {
  allowedSenders?: string[];
}

// Conversation Types
export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface UiEvent {
  eventType: string;
  data: Record<string, unknown>;
}

export interface ArtifactReference {
  artifactId: string;
  s3Key: string;
  contentType: string;
  filename: string;
  sizeBytes: number;
  url?: string;
}

/** Reference linking an operator's summary message to the delegation it responded to. */
export interface DelegationRef {
  taskId: string;
  canvasId: string;
  delegatedRunId: string;
  agentName: string;
  title: string;
  /**
   * Present when the delegation updated existing surfaces in place rather
   * than allocating a new task canvas. Each entry links back to a
   * referenced surface in `canvasId`.
   */
  surfaceRefs?: Array<{ surfaceId: string; surfaceName: string; title?: string }>;
}

export interface ConversationMessage {
  messageId: string;
  role: 'user' | 'assistant';
  content: Array<{ text: string }>;
  createdAt: string;
  /**
   * Origin discriminator for the message. Absent or `'user'` means a normal
   * composer-typed message. `'aui_action'` marks synthetic surface-action
   * messages (hidden from the thread); `'event_notification'` marks
   * platform-injected turns (e.g. the credential-continuation SYSTEM NOTE)
   * rendered as a collapsed system-event chip; `'voice'` marks spoken turns.
   * Mirrors optr-agent-core `MessageSource`; open string members may appear
   * before the frontend enumerates them.
   */
  source?: 'user' | 'aui_action' | 'event_notification' | 'inbox' | 'delegation_result' | 'voice';
  // Assistant-only fields:
  runId?: string;
  durationMs?: number;
  usage?: TokenUsage;
  artifacts?: ArtifactReference[];
  uiEvents?: UiEvent[];
  activeCanvases?: string[];
  delegationRef?: DelegationRef;
  /**
   * Links an `ask_user` question or answer turn to its request, with a
   * self-contained snapshot of the questions (and answers). Present on the
   * assistant turn (`kind: 'question'`) and the user turn (`kind: 'answer'`).
   */
  askUserRef?: AskUserRef;
}

export type ConversationOrigin = 'user' | 'background';

export interface Conversation {
  conversationId: string;
  orgId: string;
  userId?: string;
  agentId?: string;
  name?: string;
  persistent: boolean;
  /** True if pinned/starred. Pinned conversations are returned in a dedicated
   *  `pinned` array on the list endpoint and never auto-expire. */
  pinned?: boolean;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  origin?: ConversationOrigin;
}

export interface CreateConversationInput {
  agentId?: string;
  persistent?: boolean;
  origin?: ConversationOrigin;
}

export interface UpdateConversationInput {
  name?: string;
  persistent?: boolean;
  /** Pin/unpin. Pinning forces `persistent: true` and removes the TTL. */
  pinned?: boolean;
}

export interface ConversationWithMessages {
  conversationId: string;
  orgId: string;
  userId?: string;
  agentId?: string;
  name?: string;
  persistent: boolean;
  pinned?: boolean;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  origin?: ConversationOrigin;
  messages: ConversationMessage[];
  stats?: {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalRuns: number;
    totalTokens: number;
    totalToolCalls: number;
  };
  lastContextUtilization?: import('./events').ContextUtilization;
  canvases?: Array<{
    canvasId: string;
    title: string;
    isDefault: boolean;
    layout?: import('./canvases').CanvasLayout;
    order?: number;
    createdAt?: string;
    updatedAt?: string;
  }>;
}

// Platform Cortex Types
export interface PlatformCortex {
  version: number;
  prompt: string;
  toolIds: string[];
  description: string;
  createdBy: string;
  createdAt: string;
}

// Platform entity helper
export const isPlatformEntity = (entity: { accessScope?: string }) =>
  entity.accessScope === 'global';

// Schedule Types
export type { Schedule, ScheduleStatus, ScheduleType, CreateScheduleInput, UpdateScheduleInput } from './schedules';
export type { ActivityItem, ActivityScope, ActivitySource, ActivityTriggerSource, ActivityPriority, ActivityVisibility, ActivityListParams } from './activity';
export type { RunSummary, RunListParams, ToolCallDetail } from './runs';
export type {
  InboxItem,
  InboxItemStatus,
  InboxRefType,
  InboxActionLabel,
  InboxCancelReason,
  InboxItemWithSource,
  AskUserQuestion,
  AskUserQuestionInputType,
  AskUserQuestionOption,
  AskUserQuestionSurfaceRef,
  AskUserAnswer,
  AskUserRequest,
  AskUserRequestStatus,
  AskUserRef,
} from './inbox';

// Overview Types
export type {
  OverviewStats,
  OverviewLists,
  OverviewResponse,
  OverviewIncludeKey,
  DelegatedTask,
  DelegatedTaskStatus,
  DelegatedTaskSummary,
  ActiveUser,
  ActiveUsersSnapshot,
  PresenceStatus,
  RunningAgent,
  UpcomingScheduleItem,
  UpcomingSchedulesResponse,
  UpcomingSchedulesParams,
} from './overview';
